import React, { Children } from 'react';
import {
  Breadcrumb as ChakraBreadcrumb,
  BreadcrumbItem,
  BreadcrumbProps as ChakraBreadcrumbProps,
  BreadcrumbSeparator,
  forwardRef,
} from '@chakra-ui/react';
import Icon, { TypeIconName } from '../Icon/Icon';
import { useResponsive } from '../../hooks';

export interface BreadcrumbProps extends ChakraBreadcrumbProps {
  hasSeparatorAfterLast?: boolean;
  hasSeparatorBeforeFirst?: boolean;
  separatorIconName?: TypeIconName;
}

/**
 * Breadcrumbs is a navigation pattern that helps users understand the hierarchy of a website.
 */
const Breadcrumb = forwardRef<BreadcrumbProps, 'nav'>((props, ref) => {
  const { children, hasSeparatorAfterLast, hasSeparatorBeforeFirst, separatorIconName, ...rest } = props;
  const { isMobile } = useResponsive();

  const childrenCount = Children.count(children);
  const items = Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) {
      return null;
    }

    return (
      <BreadcrumbItem>
        {hasSeparatorBeforeFirst && index === 0 && <BreadcrumbSeparator marginLeft="0" />}
        {child}
        {hasSeparatorAfterLast && index === childrenCount - 1 && <BreadcrumbSeparator />}
      </BreadcrumbItem>
    );
  });

  const defaultIconName = isMobile ? 'ChevronLeft' : 'ChevronRight';
  const properties = {
    separator: <Icon name={separatorIconName || defaultIconName} size="24" />,
    ...rest,
  };
  return (
    <ChakraBreadcrumb {...properties} ref={ref}>
      {items}
    </ChakraBreadcrumb>
  );
});

export default Breadcrumb;
